import { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { SkeletonBox } from '@/components';
import { useQuotationTemplatePreviewPdf } from '../hooks';
import { resolveQuotationMediaUrl } from '../utils';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Match worker to react-pdf's pdfjs version (avoid dark native iframe chrome).
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function ThumbnailShimmer() {
  return (
    <div
      className="absolute inset-0 z-10 overflow-hidden bg-[#F0F0F0]"
      aria-hidden="true"
    >
      <SkeletonBox className="absolute inset-0 w-full h-full rounded-none" />
      <div className="absolute inset-0 z-10 p-3 flex flex-col pointer-events-none">
        <div className="h-2 w-full rounded-sm bg-white/55 mb-2.5" />
        <div className="flex gap-2 mb-3">
          <div className="h-1.5 w-1/3 rounded-sm bg-white/50" />
          <div className="h-1.5 w-1/5 rounded-sm bg-white/40 ml-auto" />
        </div>
        <div className="space-y-1.5 flex-1">
          <div className="h-1.5 w-full rounded-sm bg-white/45" />
          <div className="h-1.5 w-[90%] rounded-sm bg-white/40" />
          <div className="h-1.5 w-[75%] rounded-sm bg-white/35" />
          <div className="h-1.5 w-full rounded-sm bg-white/40" />
          <div className="h-1.5 w-[85%] rounded-sm bg-white/35" />
        </div>
        <div className="mt-auto pt-2 flex items-center justify-between border-t border-white/35">
          <div className="h-1.5 w-16 rounded-sm bg-white/45" />
          <div className="h-1.5 w-10 rounded-sm bg-white/40" />
        </div>
      </div>
    </div>
  );
}

function ThemeFallback({ template, className = '' }) {
  return (
    <div
      className={`w-full h-full flex flex-col justify-between p-3 select-none ${className}`}
      style={{
        background: `linear-gradient(145deg, ${
          template?.theme_config?.primary_color || '#1F2241'
        } 0%, ${template?.theme_config?.accent_color || '#50AB3D'} 100%)`,
      }}
    >
      <span className="text-[9px] uppercase font-bold tracking-wider text-white/80">
        {template?.category || 'Quotation'}
      </span>
      <div>
        <h5 className="text-xs font-bold text-white truncate drop-shadow-xs">
          {template?.name}
        </h5>
        <p className="text-[10px] text-white/80 mt-0.5">PDF Design</p>
      </div>
    </div>
  );
}

/**
 * Card thumbnail via react-pdf canvas (no native PDF iframe — that chrome is dark gray).
 * Prefer static preview_image_url when available.
 */
export function TemplatePdfThumbnail({
  companyId,
  template,
  priority = 0,
  className = '',
}) {
  const templateKey = template?.key;
  const imageUrl = resolveQuotationMediaUrl(template?.preview_image_url);
  const containerRef = useRef(null);
  const [imageReady, setImageReady] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [pageReady, setPageReady] = useState(false);
  const [pdfFailed, setPdfFailed] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);

  const usePdf = !imageUrl || imageFailed;

  const { blobUrl, isLoading, isError } = useQuotationTemplatePreviewPdf(
    companyId,
    templateKey,
    {
      enabled: Boolean(usePdf && companyId && templateKey),
      priority,
    },
  );

  useEffect(() => {
    setImageReady(false);
    setImageFailed(false);
  }, [imageUrl]);

  useEffect(() => {
    setPageReady(false);
    setPdfFailed(false);
  }, [blobUrl]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setContainerWidth(Math.floor(w));
    });
    ro.observe(el);
    setContainerWidth(Math.floor(el.clientWidth) || 0);
    return () => ro.disconnect();
  }, [usePdf]);

  if (imageUrl && !imageFailed) {
    return (
      <div
        className={`w-full h-full overflow-hidden bg-[#F0F0F0] relative select-none pointer-events-none ${className}`}
      >
        {!imageReady && <ThumbnailShimmer />}
        <img
          src={imageUrl}
          alt=""
          className={`w-full h-full object-cover object-top transition-opacity duration-200 ${
            imageReady ? 'opacity-100' : 'opacity-0'
          }`}
          loading="lazy"
          onLoad={() => setImageReady(true)}
          onError={() => setImageFailed(true)}
        />
      </div>
    );
  }

  if (isError || pdfFailed) {
    return <ThemeFallback template={template} className={className} />;
  }

  const showShimmer = isLoading || !blobUrl || !pageReady;

  return (
    <div
      ref={containerRef}
      className={`w-full h-full overflow-hidden bg-[#F0F0F0] relative select-none pointer-events-none ${className}`}
    >
      {showShimmer && <ThumbnailShimmer />}
      {blobUrl && containerWidth > 0 ? (
        <div
          className={`absolute inset-0 overflow-hidden bg-white transition-opacity duration-200 ${
            pageReady ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <Document
            file={blobUrl}
            loading={null}
            error={null}
            onLoadError={() => setPdfFailed(true)}
            onSourceError={() => setPdfFailed(true)}
          >
            <Page
              pageNumber={1}
              width={containerWidth}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              loading={null}
              onRenderSuccess={() => setPageReady(true)}
              onRenderError={() => setPdfFailed(true)}
            />
          </Document>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Full-size sample PDF viewer for Preview modal.
 * Canvas render + shimmer until first page paints (no dark iframe chrome).
 */
export function TemplatePdfViewer({
  companyId,
  templateKey,
  templateName = 'Template',
  enabled = true,
  onErrorFallback = null,
}) {
  const containerRef = useRef(null);
  const [pageReady, setPageReady] = useState(false);
  const [pdfFailed, setPdfFailed] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);

  const { blobUrl, isLoading, isError, refetch } =
    useQuotationTemplatePreviewPdf(companyId, templateKey, {
      enabled: Boolean(enabled && companyId && templateKey),
      priority: 25,
    });

  useEffect(() => {
    setPageReady(false);
    setPdfFailed(false);
  }, [blobUrl, templateKey]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setContainerWidth(Math.floor(w));
    });
    ro.observe(el);
    setContainerWidth(Math.floor(el.clientWidth) || 0);
    return () => ro.disconnect();
  }, []);

  const failed = isError || pdfFailed;
  const showShimmer =
    !failed && (isLoading || !blobUrl || !pageReady || containerWidth <= 0);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full min-h-[320px] overflow-hidden bg-[#F0F0F0]"
    >
      {showShimmer && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center">
          <ThumbnailShimmer />
          <span className="relative z-20 text-xs font-medium text-text-secondary bg-white/90 px-3 py-1.5 rounded-md shadow-sm">
            Loading sample PDF…
          </span>
        </div>
      )}

      {failed && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 p-6 text-center bg-[#F8FAF8]">
          <p className="text-sm text-text-secondary">
            Could not load sample PDF preview.
          </p>
          <button
            type="button"
            className="text-xs font-semibold px-3 py-1.5 rounded-md border border-border-default bg-white hover:bg-[#F5F5F5]"
            onClick={() => {
              setPdfFailed(false);
              setPageReady(false);
              refetch?.();
            }}
          >
            Retry
          </button>
          {onErrorFallback}
        </div>
      )}

      {blobUrl && containerWidth > 0 && !failed ? (
        <div
          className={`w-full h-full overflow-auto bg-white custom-scrollbar transition-opacity duration-200 ${
            pageReady ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <Document
            file={blobUrl}
            loading={null}
            error={null}
            onLoadError={() => setPdfFailed(true)}
            onSourceError={() => setPdfFailed(true)}
          >
            <Page
              pageNumber={1}
              width={containerWidth}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              loading={null}
              onRenderSuccess={() => setPageReady(true)}
              onRenderError={() => setPdfFailed(true)}
              className="mx-auto"
            />
          </Document>
        </div>
      ) : null}

      <span className="sr-only">{templateName} sample PDF</span>
    </div>
  );
}

export default TemplatePdfThumbnail;
